<template>
  <div class="general-script-main">
    <h3>{{$t('message.script_config_zabbix') | title}}</h3>

    <div v-if="willRunOnEachClusterNode" class="cluster-info">
      {{ $t('message.will_run_on_each_cluster_node') | title }}
    </div>
    <div v-if="willRunOnThisClusterNodeOnly" class="cluster-info">
      {{ $t('message.will_run_on_this_cluster_node_only') | title }}
    </div>

    <b-form v-if="zabbixConfig">
      <b-form-row>
        <div class="col-3">
          <span class="label label-default">
            {{ $t("message.zabbix_enabled") | title }}
          </span>
        </div>
        <div class="col-8">
          <div class="form-group form-check">
            <input type="checkbox" class="form-check-input" v-model="zabbixConfig.enabled"/>
          </div>
        </div>
      </b-form-row>

      <b-form-row>
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_hostname') | title}}</span>
        </div>
        <div class="col-8">
          <b-form-input v-model="zabbixConfig.hostname" type="string"/>
        </div>
      </b-form-row>

      <b-form-row>
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_servers_active') | title}}</span>
        </div>
        <div class="col-8">
          <tags-input ref="server-active-tag" v-model="zabbixConfig.server_active"
            :typeahead="false" :add-tags-on-comma="true" :add-tags-on-blur="true" placeholder=""/>
        </div>
      </b-form-row>

      <b-form-row>
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_servers_passive') | title}}</span>
        </div>
        <div class="col-8">
          <tags-input ref="server-tag" v-model="zabbixConfig.server"
            :typeahead="false" :add-tags-on-comma="true" :add-tags-on-blur="true" placeholder=""/>
        </div>
      </b-form-row>

      <b-form-row>
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_listen_port') | title}}</span>
        </div>
        <div class="col-8">
          <b-form-input type="number" min="1" v-model="zabbixConfig.listen_port"/>
        </div>
      </b-form-row>

      <b-form-row>
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_encryption') | title}}</span>
        </div>
        <div class="col-8">
          <b-form-select v-model="zabbixConfig.encryption" :options="encryptionOptions" class="col-4"/>
        </div>
      </b-form-row>

      <b-form-row v-if="zabbixConfig.encryption=='psk'">
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_psk_identity') | title}}</span>
        </div>
        <div class="col-8">
          <b-form-input v-model="zabbixConfig.psk_identity"/>
        </div>
      </b-form-row>

      <b-form-row v-if="zabbixConfig.encryption=='psk'">
        <div class="col-3">
          <span class="label label-default">{{$t('message.zabbix_psk') | title}}</span>
        </div>
        <div class="col-8">
          <b-form-input v-model="zabbixConfig.psk" :placeholder='$t("message.zabbix_leave_empty_to_generate")'/>
        </div>
      </b-form-row>

      <div v-if="formError" class="form-error">{{formError}}</div>

      <b-button variant="primary" type="button"
        class="padmin-button submit-button right" :disabled="runInProgress" @click="save">
        <b-spinner v-if="runInProgress" small type="grow"/>
        {{$t('message.button_save') | title}}
      </b-button>

    </b-form>

    <ConsoleOutput v-if="result != null" :result="result" :output="output"/>

  </div>
</template>

<script>
import {fromKeyValueToArray, arrayToKeyValue} from '../util'
import { get, post } from "../http"
import ConsoleOutput from "./ConsoleOutput"
import {validateServerNamePort, validateAlphanumeric, validateIpAddress, validatePort, validateHostName} from "../util"

export default {
  name: "ZabbixConfig",

  components: {
    ConsoleOutput
  },

  data() {
    return {
      zabbixConfig: null,
      result: null,
      output: "",
      formError: '',
      runInProgress: false,
      encryptionOptions: ['unencrypted', 'psk']
    };
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      get('/zabbix_config').then(
        resp => {
          this.zabbixConfig = resp.data
          this.zabbixConfig.server = arrayToKeyValue(this.zabbixConfig.server)
          this.zabbixConfig.server_active = arrayToKeyValue(this.zabbixConfig.server_active)
          this.willRunOnEachClusterNode = resp.data.will_run_on_each_cluster_node
          this.willRunOnThisClusterNodeOnly = resp.data.will_run_on_this_cluster_node_only
        },
        err => {
          console.log('err', err)
        })
    },

    save() {
      this.formError = ''

      let data = JSON.parse(JSON.stringify(this.zabbixConfig))
      data.server = fromKeyValueToArray(data.server)
      data.server_active = fromKeyValueToArray(data.server_active)

      if (data.enabled && data.server.length == 0 && data.server_active.length == 0) {
        this.formError = this.$t("message.zabbix_either_passive_or_active_server")
        return
      }

      if (this.zabbixConfig.psk && !validateAlphanumeric(data.psk)) {
        this.formError = this.$t("message.error_invalid_psk")
        return
      }

      if (!validateHostName(data.hostname)) {
        this.formError = this.$t("message.error_invalid_hostname") + ": " + data.hostname
        return
      }

      data.server.forEach(serv => {
        if (!validateIpAddress(serv)) {
            this.formError = this.$t("message.error_invalid_servername") + ": " + serv
            return
        }
      })

      data.server_active.forEach(serv => {
        if (!validateServerNamePort(serv)) {
            this.formError = this.$t("message.error_invalid_servername") + ": " + serv
            return
        }
      })

      if (!validatePort(data.listen_port)) {
        this.formError = this.$t("message.error_invalid_port") + ": " + data.listen_port
        return
      }

      this.result = null
      this.runInProgress = true
      this.output = ''

      post('/zabbix_config', data, { "Content-Type": "application/json" }).then(
        resp => {
          this.result = resp.data.result
          this.output = resp.data.output
          this.runInProgress = false
          this.load()
        },
        err => {
          console.log('err', err);
          this.runInProgress = false
          this.result = false
        }
      )
    }
  }
}
</script>

<style>
@import "./../styles/zabbixComponent.css"
</style>
